import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * Eligibility screening — Inclusion and Exclusion Criteria Policy §5, §6.
 *
 * The verdict comes from the server, not from here. A browser could decide it
 * faster, but §7.6 requires a record of the criterion applied and §9 audits those
 * decisions monthly for consistency and quarterly against protected
 * characteristics — none of which is possible if the screening happens in a tab
 * and only its answer is stored.
 */

export interface EligibilityAnswers {
  dateOfBirth: string;
  ukResident: boolean | null;
  gpRegistered: boolean | null;
  gpPracticeName?: string;
  ableToTakePartRemotely: boolean | null;
  seekingSymptomDiagnosis: boolean | null;
  needsPrescription: boolean | null;
  eatingDisorder: boolean | null;
  mentalHealthCrisis: boolean | null;
  pregnant: boolean | null;
  urgentConcern: boolean | null;
}

export interface EligibilityFlag {
  key: string;
  clause: string;
  label: string;
  deferral: boolean;
  route: string | null;
}

export interface EligibilityScreeningResult {
  ok: boolean;
  outcome: 'accepted' | 'blocked' | 'needs_clinical_review';
  blockers: EligibilityFlag[];
  flags: EligibilityFlag[];
  /** §6.8 / §6.3 — disclosures that get their signposting now, not after review. */
  immediate: string[];
}

const screenFn = httpsCallable<{ answers: EligibilityAnswers }, EligibilityScreeningResult>(
  functions,
  'recordEligibilityScreening',
);

export async function recordEligibilityScreening(
  answers: EligibilityAnswers,
): Promise<EligibilityScreeningResult> {
  const res = await screenFn({ answers });
  return res.data;
}

/** The questions, in the order they are asked. */
export interface ScreeningQuestion {
  key: keyof EligibilityAnswers;
  prompt: string;
  help?: string;
  /** Which answer flags. Most of these read better as "yes flags". */
  flagOn: boolean;
}

/**
 * Worded as things a person would say about themselves, not as exclusion criteria.
 *
 * §6.12 is the reason this matters: a protected characteristic, a disability, low
 * digital confidence and needing an interpreter are explicitly NOT grounds for
 * exclusion, so the form must not read like a list of ways to be turned away. The
 * two questions about access exist to trigger §8's adjustments, and say so.
 */
export const SCREENING_QUESTIONS: ScreeningQuestion[] = [
  {
    key: 'urgentConcern',
    prompt: 'Do you have a health concern right now that feels urgent?',
    help: 'Melio is a scheduled service and does not provide urgent or out-of-hours care.',
    flagOn: true,
  },
  {
    key: 'seekingSymptomDiagnosis',
    prompt: 'Are you joining mainly to find out what is causing a symptom you have now?',
    help: 'Your GP is the right person for that. Once a symptom has been looked at, preventive '
      + 'assessment with Melio may be a good fit.',
    flagOn: true,
  },
  {
    key: 'needsPrescription',
    prompt: 'Are you looking for a prescription, or for Melio to manage your medication?',
    help: 'Melio does not prescribe, supply or change medicines. That stays with your GP or '
      + 'specialist.',
    flagOn: true,
  },
  {
    key: 'eatingDisorder',
    prompt: 'Have you ever had an eating disorder, or do you have one now?',
    help: 'Melio is built on repeated measurement of weight and blood markers, which can make '
      + 'things harder rather than better. We ask so we can talk it through with you.',
    flagOn: true,
  },
  {
    key: 'mentalHealthCrisis',
    prompt: 'Are you in crisis right now, or having thoughts of ending your life?',
    flagOn: true,
  },
  {
    key: 'pregnant',
    prompt: 'Are you pregnant?',
    help: 'Many of the markers Melio measures cannot be read reliably in pregnancy, so we would '
      + 'pick this up after the postnatal period.',
    flagOn: true,
  },
  {
    key: 'ableToTakePartRemotely',
    prompt: 'Are you able to take part in a video or telephone appointment?',
    help: 'If not, say so — Melio can offer a phone call instead of video, a longer appointment, '
      + 'an interpreter, someone with you, or help setting things up first.',
    flagOn: false,
  },
  {
    key: 'gpRegistered',
    prompt: 'Are you registered with an NHS GP?',
    help: 'Melio works alongside your GP rather than replacing them. If you are not registered, '
      + 'we can help you do it.',
    flagOn: false,
  },
];
